import React from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, Redirect, withRouter } from 'react-router-dom';
import { SUBTABS, SETUP_STEPS } from './ConstPaths';
import _ from 'lodash';
import TestDriveSetupIntro from './TestDriveSetupIntro';
import TestDriveSetupSelectionMethod from './TestDriveSetupSelectionMethod';
import TestDriveSetupBodySites from './TestDriveSetupBodySites';
import TestDriveSetupAccessories from './TestDriveSetupAccessories';
import TestDriveSetupInfo from './TestDriveSetupInfo';
import TestDriveSetupName from './TestDriveSetupName';
import TestDriveSetupSandbox from './TestDriveSetupSandbox';
import TestDriveSetupReview from './TestDriveSetupReview';
import { findOtherIndex, getOtherEntryFromArray, getOtherEntryFromString, OTHER_CKBX_VAL, OTHER_DISPLAY_SEP } from './OtherCheckboxUtils';

const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  isLowTechPath: PropTypes.bool,
  bodySites: PropTypes.arrayOf(PropTypes.string),
  updateActiveTestDriveId: PropTypes.func.isRequired,
  activeTestDrive: PropTypes.object,
  updateSession: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  isLowTechPath: false,
  bodySites: [],
  activeTestDrive: {}
};

//Manage the wizard form for the Test-drive / Choose sub-tab
//Initialize the forms with props and manage those as local state.
// Following pattern described in https://goshacmd.com/wizard-form-collect-info/

//the steps of the wizard form:
const steps = SETUP_STEPS;

const BASE_URL = SUBTABS.SETUP + "/";
const NEXT_SECTION = SUBTABS.TRY;

class TestDriveSetup extends React.Component {
  constructor(props) {
    super(props);

    //manage the incoming props  - those that can be changed in this section - as local state
    this.state = {
      prevActiveTestDrive: this.props.activeTestDrive, //for getDerivedStateFromProps
      activeTestDrive: this.props.activeTestDrive,
      //local state for 'other' entries
      otherSelectionMethod: getOtherEntryFromString(this.props.activeTestDrive.activeSetupSelectionMethod),
      otherAccessory: getOtherEntryFromArray(this.props.activeTestDrive.activeSetupAccessories),
    };

    this.saveActiveSetupSelectionMethodToSession = this.saveActiveSetupSelectionMethodToSession.bind(this);
    this.saveActiveSetupBodySiteInfoToSession = this.saveActiveSetupBodySiteInfoToSession.bind(this);
    this.saveActiveSetupAccessoriesToSession = this.saveActiveSetupAccessoriesToSession.bind(this);
    this.saveSetupInfoToSession = this.saveSetupInfoToSession.bind(this);
    this.saveActiveSetupNameToSession = this.saveActiveSetupNameToSession.bind(this);
    this.onContinue = this.onContinue.bind(this);
    console.log("constructed TestDriveSetup, with state: " + JSON.stringify(this.state));

  }

  //Used both here and in TestDriveTry in order to allow editing setup info from both sections.
  //https://reactjs.org/docs/react-component.html
  //getDerivedStateFromProps is invoked right before calling the render method, both on the initial mount and on subsequent updates. 
  //It should return an object to update the state, or null to update nothing.
  //Enables a component to update its internal state as the result of changes in props.
  //Allows the /try/update-setup component to stay in synch with any setup info changes that happen in the /setup/info form.
  //WHY WE NEED THIS:
  //When you move from /setup/info *directly* to the /try section, TestDriveTry is actually constructed BEFORE
  //TestDriveSetupInfo unmounts.  So if you jump directly to the /try section,
  //the data saved on the unmount won't be immediately available to TestDriveTry.
  //Once TestDriveSetupInfo unmounts, its data are saved and passed in as changed props to TestDriveTry, 
  //and then this method updates the state accordingly.
  //ANOTHER TEST FOR THIS:
  //start at test-drive/choose/access-method. Choose a keyboard (something that only has direct as selection metho)
  //hit Continue -- the sidebar should then show the access method ('keyboard') and selection method ('direct')
  //If this is NOT working, the sidebar selection method will say 'None entered'
  static getDerivedStateFromProps(props, state) {
    console.log("called getDerivedStateFromProps, props: " + JSON.stringify(props) + ", state: " + JSON.stringify(state));
    //https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html
    // Any time the setup info props change,
    // Reset any parts of state that are tied to that setup info.
    if (!_.isEqual(props.activeTestDrive, state.prevActiveTestDrive)){
      return {
        prevActiveTestDrive: {...props.activeTestDrive},
        activeTestDrive: {...props.activeTestDrive},
        otherSelectionMethod: getOtherEntryFromString(props.activeTestDrive.activeSetupSelectionMethod),
        otherAccessory: getOtherEntryFromArray(props.activeTestDrive.activeSetupAccessories)
      };
    }
    return null;
  }

  //Get the current step from the URL
  getCurrentStep(){
    //e.g. URL of '/gather-info/contexts/intro' would have step of 'intro'
    //props.match: {"step":"intro"}
    const stepParam = this.props.match.params.step;
    if(stepParam){
      return(stepParam);
    }
    else{
      return(steps.INTRO);
    }
  }

  //On Continue for a given step:
  // figure out the next step and go to it
  // and if we're totally done with this section, submit the data.
  onContinue() {
    let history = this.props.history;
    const isLowTechPath = this.props.isLowTechPath;
    const activeAccessMethodName = this.props.activeTestDrive.activeAccessMethodName;
    const activeAccessMethodSelectionMethods = this.props.activeTestDrive.activeAccessMethodSelectionMethods;
    let nextStep = '';
    let nextRoute = '';
    let goToNextSection = false;
    const curURL = new URL(window.location.href);


    //visit selection methods if there's an array with more than one selection method associated with the chosen access method.
    //i.e., a Keyboard only has Direct Select method, so there's no point visiting the selection methods page.
    //also if user hasn't picked an access method (in that case we just suggest they choose one, but let them go through setup regardless)
    //And always visit selection methods in the low-tech case (even a Switch has 2 possible methods in that case - direct and partner-assisted scanning)
    const visitSelectionMethods = 
      (isLowTechPath || 
      (!activeAccessMethodName) ||
      ((Array.isArray(activeAccessMethodSelectionMethods) && activeAccessMethodSelectionMethods.length > 1)));

    switch(this.getCurrentStep()){
      case steps.INTRO:
        nextStep = visitSelectionMethods ? steps.SELECTION_METHOD : steps.BODY_SITES;
        break;
      case steps.SELECTION_METHOD:
        nextStep = steps.BODY_SITES;
        break;
      case steps.BODY_SITES:
        nextStep = steps.ACCESSORIES;
        break;
      case steps.ACCESSORIES:
        nextStep = steps.INFO;
        break;
      case steps.INFO:
        nextStep = steps.NAME;
        break;
      case steps.NAME:
        nextStep = isLowTechPath ? steps.REVIEW : steps.SANDBOX;
        break;
      case steps.SANDBOX:
        nextStep = steps.REVIEW;
        break;
      case steps.REVIEW:
        nextStep = '';
        goToNextSection = true;
        break;
      default:
        console.log("   default next step");
        nextStep = steps.INFO;
    }
    if (!goToNextSection) {
      nextRoute = BASE_URL + nextStep;
    } 
    else {
      // alert("Done with this section. Data saved are: " + JSON.stringify(this.state));
      nextRoute = NEXT_SECTION;
    }
    console.log("    pushing new route: " + nextRoute);
    history.push({
      pathname: nextRoute,
      search: curURL.search
    });
  }

  // methods passed in to child routes to save data.
  // each one also checks to see if we need to create a new test-drive id
  saveActiveSetupSelectionMethodToSession(){
    this.props.updateActiveTestDriveId();
    let newActiveTestDrive = {
      ...this.state.activeTestDrive,
      activeSetupSelectionMethod: this.state.activeTestDrive.activeSetupSelectionMethod,
    }
    this.props.updateSession('activeTestDrive', newActiveTestDrive );
  }
  saveActiveSetupBodySiteInfoToSession(){
    this.props.updateActiveTestDriveId();
    let newActiveTestDrive = {
      ...this.state.activeTestDrive,
      activeSetupBodySite: this.state.activeTestDrive.activeSetupBodySite,
      activeSetupBodySiteComments: this.state.activeTestDrive.activeSetupBodySiteComments
    }
    this.props.updateSession('activeTestDrive', newActiveTestDrive);
  }
  saveActiveSetupAccessoriesToSession(){
    this.props.updateActiveTestDriveId();
    let newActiveTestDrive = {
      ...this.state.activeTestDrive,
      activeSetupAccessories: this.state.activeTestDrive.activeSetupAccessories,
    }
    this.props.updateSession('activeTestDrive', newActiveTestDrive);
  }
  saveSetupInfoToSession(){
    this.props.updateActiveTestDriveId();
    let newActiveTestDrive = {
      ...this.state.activeTestDrive,
      activeSetupPositioningNotes: this.state.activeTestDrive.activeSetupPositioningNotes,
      activeSetupOtherNotes: this.state.activeTestDrive.activeSetupOtherNotes
    }
    this.props.updateSession('activeTestDrive', newActiveTestDrive);
  }
  saveActiveSetupNameToSession(){
    this.props.updateActiveTestDriveId();
    let newActiveTestDrive = {
      ...this.state.activeTestDrive,
      activeSetupName: this.state.activeTestDrive.activeSetupName,
    }
    this.props.updateSession('activeTestDrive', newActiveTestDrive);
  }

  //the keys are the names of the local state variables, defined above in the constructor
  updateLocalState(key) {
    return evt => {
      const target = evt.target; /* which input has been changed */
      let value = target.type === 'checkbox' ? target.checked: target.value;
      //handle 'bodySites' differently, since that holds an array rather than a single value
      if(key === 'activeSetupBodySite'){
        const newItem=target.value;
        const newItemIndex = this.state.activeTestDrive.activeSetupBodySite.indexOf(newItem);
        let activeSetupBodySite = [...this.state.activeTestDrive.activeSetupBodySite];
        //if an activity is checked: add it, if not already in list
        if((value===true) && (newItemIndex === -1)) {
          activeSetupBodySite.push(newItem);
        }
        //activity unchecked: remove it, if already in the list
        else if ((value===false) && (newItemIndex !== -1)){
          activeSetupBodySite.splice(newItemIndex, 1);
        }
        value = activeSetupBodySite;
      }
      //handle 'accessories' differently, since that holds an array rather than a single value
      if(key === 'activeSetupAccessories'){
        const newItem=target.value;
        const newItemIndex = this.state.activeTestDrive.activeSetupAccessories.findIndex((acc) => acc.startsWith(newItem));
        let activeSetupAccessories = [...this.state.activeTestDrive.activeSetupAccessories];
        //if an activity is checked: add it, if not already in list
        if((value===true) && (newItemIndex === -1)) {
          activeSetupAccessories.push(newItem);
        }
        //activity unchecked: remove it, if already in the list
        else if ((value===false) && (newItemIndex !== -1)){
          activeSetupAccessories.splice(newItemIndex, 1);
          if( newItem === OTHER_CKBX_VAL)
            this.setState({ 'otherAccessory': ""});
        }
        value = activeSetupAccessories;
      }
      if(key === 'activeSetupSelectionMethod'){
        const newItem=target.value;
        //reset the text string for 'other', if something other than Other was checked
        if(this.state.otherSelectionMethod && (newItem !== OTHER_CKBX_VAL)){
          this.setState({ 'otherSelectionMethod': ""});
        }
      }
      console.log("Updating TestDriveSetup state with key: " + key + " value: " + value);

      //maintain the local state for the 'other' text fields (these will then set activeTestDrive in their callbacks)
      if (key === 'otherAccessory')
        this.setState({ [key]: value }, this.adjustAccessories);
      else if (key === 'otherSelectionMethod')
        this.setState({ [key]: value }, this.adjustSelectionMethod);
      //for all other keys, update the activeTestDrive with the new value
      else{
        this.setState({
          activeTestDrive: {
            ...this.state.activeTestDrive,
            [key]: value
          }
        });
      }
    }
  }

  //after setting 'otherAccessory', adjust the activities array (in activeTestDrive) to include it.
  adjustAccessories(){
    let otherAccessory = OTHER_CKBX_VAL + OTHER_DISPLAY_SEP + this.state.otherAccessory;
    let accessories = [...this.state.activeTestDrive.activeSetupAccessories];
    const otherItemIndex = findOtherIndex(this.state.activeTestDrive.activeSetupAccessories);
    if( otherItemIndex > -1){
      accessories[otherItemIndex] = otherAccessory;
      this.setState({ 
        activeTestDrive: {
          ...this.state.activeTestDrive,
          'activeSetupAccessories': accessories
        }
      });
    }
  }

  //Set activeTestDrive selectionMethod to "Other - [text from this.state.otherSelectionMethod]"
  adjustSelectionMethod(){
    console.log("in adjustSelectionMethod, this.state.otherSelectionMethod: " + this.state.otherSelectionMethod);
    let otherSelectionMethod = OTHER_CKBX_VAL + OTHER_DISPLAY_SEP + this.state.otherSelectionMethod;
    this.setState({ 
      activeTestDrive: {
        ...this.state.activeTestDrive,
        'activeSetupSelectionMethod': otherSelectionMethod
      }
    });
  }

  //If new props come in after rendering, update the views in this tab.
  //(This occurs when you refresh a view and it pulls data from the database.)
  componentDidUpdate(prevProps, prevState){
    //for values that are Arrays:
    // if (_.isEqual(this.props.activeSetupBodySite, prevProps.activeSetupBodySite) === false){
    //   console.log("--incoming props: " + this.props.activeSetupBodySite + " differ from prev props: " + prevProps.activeSetupBodySite);
    //   this.setState({'activeSetupBodySite': this.props.activeSetupBodySite});
    // };
    // if (_.isEqual(this.props.activeSetupAccessories, prevProps.activeSetupAccessories) === false){
    //   console.log("--incoming props: " + this.props.activeSetupAccessories + " differ from prev props: " + prevProps.activeSetupAccessories);
    //   this.setState({'activeSetupAccessories': this.props.activeSetupAccessories});
    // };
    
    // //for simple strings
    // if (this.props.activeSetupSelectionMethod !== prevProps.activeSetupSelectionMethod){
    //   console.log("--incoming props: " + this.props.activeSetupSelectionMethod + " differ from prev props: " + prevProps.activeSetupSelectionMethod);
    //   this.setState({'activeSetupSelectionMethod': this.props.activeSetupSelectionMethod});
    // }
    // if (this.props.activeSetupBodySiteComments !== prevProps.activeSetupBodySiteComments){
    //     console.log("--incoming props: " + this.props.activeSetupBodySiteComments + " differ from prev props: " + prevProps.activeSetupBodySiteComments);
    //     this.setState({'activeSetupBodySiteComments': this.props.activeSetupBodySiteComments});
    // }
    // if (this.props.activeSetupPositioningNotes !== prevProps.activeSetupPositioningNotes){
    //   console.log("--incoming props: " + this.props.activeSetupPositioningNotes + " differ from prev props: " + prevProps.activeSetupPositioningNotes);
    //   this.setState({'activeSetupPositioningNotes': this.props.activeSetupPositioningNotes});
    // }
    // if (this.props.activeSetupOtherNotes !== prevProps.activeSetupOtherNotes){
    //   console.log("--incoming props: " + this.props.activeSetupOtherNotes + " differ from prev props: " + prevProps.activeSetupOtherNotes);
    //   this.setState({'activeSetupOtherNotes': this.props.activeSetupOtherNotes});
    // }
    // if (this.props.activeSetupName !== prevProps.activeSetupName){
    //   console.log("--incoming props: " + this.props.activeSetupName + " differ from prev props: " + prevProps.activeSetupName);
    //   this.setState({'activeSetupName': this.props.activeSetupName});
    // }
  }

  render() {
    console.log("rendering TestDriveSetup, with props: " + JSON.stringify(this.props.activeTestDrive.activeSetupSelectionMethod));
    //props that cannot be changed in this route:
    const firstName = this.props.firstName;
    const pronouns = this.props.pronouns;
    const isLowTechPath = this.props.isLowTechPath;
    const bodySites = this.props.bodySites;
    const activeAccessMethodName = this.props.activeTestDrive.activeAccessMethodName;
    const activeAccessMethodCategory = this.props.activeTestDrive.activeAccessMethodCategory;
    const activeAccessMethodSelectionMethods = this.props.activeTestDrive.activeAccessMethodSelectionMethods;
    const curURL = new URL(window.location.href);
    const searchQuery = curURL.search;
    const dev = this.props.dev;

    return(
    <>
    <Switch>
      <Route path={BASE_URL+ steps.INTRO}>
        <TestDriveSetupIntro
          key={steps.INTRO}
          onContinue={this.onContinue}
          activeAccessMethodName={activeAccessMethodName}
          activeSetupSelectionMethod={this.state.activeTestDrive.activeSetupSelectionMethod}
          activeSetupBodySite={this.state.activeTestDrive.activeSetupBodySite}
          activeSetupAccessories={this.state.activeTestDrive.activeSetupAccessories}
          activeSetupName={this.state.activeTestDrive.activeSetupName}
          dev={dev}
        />
      </Route>
      <Route path={BASE_URL+ steps.SELECTION_METHOD}>
        <TestDriveSetupSelectionMethod
          key={steps.SELECTION_METHOD}
          firstName={firstName}
          isLowTechPath={isLowTechPath}
          onContinue={this.onContinue}
          availableMethods={activeAccessMethodSelectionMethods}
          activeAccessMethodName={activeAccessMethodName}
          activeAccessMethodCategory={activeAccessMethodCategory}
          activeSetupBodySite={this.state.activeTestDrive.activeSetupBodySite}
          activeSetupAccessories={this.state.activeTestDrive.activeSetupAccessories}
          activeSetupName={this.state.activeTestDrive.activeSetupName}
          activeSetupSelectionMethod={this.state.activeTestDrive.activeSetupSelectionMethod}
          onChangeActiveSetupSelectionMethod={this.updateLocalState("activeSetupSelectionMethod")}
          otherSelectionMethod={this.state.otherSelectionMethod}
          onChangeOtherSelectionMethod={this.updateLocalState("otherSelectionMethod")}
          saveFormData={this.saveActiveSetupSelectionMethodToSession}
          dev={dev}
        />
      </Route>
      <Route path={BASE_URL+ steps.BODY_SITES}>
        <TestDriveSetupBodySites
          key={steps.BODY_SITES}
          firstName={firstName}
          bodySites={bodySites}
          onContinue={this.onContinue}
          activeAccessMethodName={activeAccessMethodName}
          activeSetupSelectionMethod={this.state.activeTestDrive.activeSetupSelectionMethod}
          activeSetupAccessories={this.state.activeTestDrive.activeSetupAccessories}
          activeSetupName={this.state.activeTestDrive.activeSetupName}
          activeSetupBodySite={this.state.activeTestDrive.activeSetupBodySite}
          onChangeActiveSetupBodySite={this.updateLocalState("activeSetupBodySite")}
          activeSetupBodySiteComments={this.state.activeTestDrive.activeSetupBodySiteComments}
          onChangeActiveSetupBodySiteComments={this.updateLocalState("activeSetupBodySiteComments")}
          saveFormData={this.saveActiveSetupBodySiteInfoToSession}
          dev={dev}
        />
      </Route>
      <Route path={BASE_URL+ steps.ACCESSORIES}>
        <TestDriveSetupAccessories
          key={steps.ACCESSORIES}
          onContinue={this.onContinue}
          activeAccessMethodName={activeAccessMethodName}
          activeSetupSelectionMethod={this.state.activeTestDrive.activeSetupSelectionMethod}
          activeSetupBodySite={this.state.activeTestDrive.activeSetupBodySite}
          activeSetupName={this.state.activeTestDrive.activeSetupName}
          activeSetupAccessories={this.state.activeTestDrive.activeSetupAccessories}
          onChangeActiveSetupAccessories={this.updateLocalState("activeSetupAccessories")}
          otherAccessory={this.state.otherAccessory}
          onChangeOtherAccessory={this.updateLocalState("otherAccessory")}
          saveFormData={this.saveActiveSetupAccessoriesToSession}
          dev={dev}
        />
      </Route>
      <Route path={BASE_URL+ steps.INFO}>
        <TestDriveSetupInfo
          key={steps.INFO}
          onContinue={this.onContinue}
          activeAccessMethodName={activeAccessMethodName}
          activeSetupSelectionMethod={this.state.activeTestDrive.activeSetupSelectionMethod}
          activeSetupBodySite={this.state.activeTestDrive.activeSetupBodySite}
          activeSetupAccessories={this.state.activeTestDrive.activeSetupAccessories}
          activeSetupName={this.state.activeTestDrive.activeSetupName}
          activeSetupPositioningNotes={this.state.activeTestDrive.activeSetupPositioningNotes}
          activeSetupOtherNotes={this.state.activeTestDrive.activeSetupOtherNotes}
          onChangeActiveSetupPositioningNotes={this.updateLocalState("activeSetupPositioningNotes")}
          onChangeActiveSetupOtherNotes={this.updateLocalState("activeSetupOtherNotes")}
          saveFormData={this.saveSetupInfoToSession}
          dev={dev}
        />
      </Route>
      <Route path={BASE_URL+ steps.NAME}>
        <TestDriveSetupName
          key={steps.NAME}
          onContinue={this.onContinue}
          activeAccessMethodName={activeAccessMethodName}
          activeSetupSelectionMethod={this.state.activeTestDrive.activeSetupSelectionMethod}
          activeSetupBodySite={this.state.activeTestDrive.activeSetupBodySite}
          activeSetupAccessories={this.state.activeTestDrive.activeSetupAccessories}
          activeSetupPositioningNotes={this.state.activeTestDrive.activeSetupPositioningNotes}
          activeSetupOtherNotes={this.state.activeTestDrive.activeSetupOtherNotes}
          activeSetupName={this.state.activeTestDrive.activeSetupName}
          onChangeActiveSetupName={this.updateLocalState("activeSetupName")}
          saveFormData={this.saveActiveSetupNameToSession}
          dev={dev}
        />
      </Route>
      <Route path={BASE_URL + steps.SANDBOX}>
        <TestDriveSetupSandbox
          key={steps.SANDBOX}
          firstName={firstName}
          activeAccessMethodName={activeAccessMethodName}
          activeSetupSelectionMethod={this.state.activeTestDrive.activeSetupSelectionMethod}
          activeSetupBodySite={this.state.activeTestDrive.activeSetupBodySite}
          activeSetupAccessories={this.state.activeTestDrive.activeSetupAccessories}
          activeSetupName={this.state.activeTestDrive.activeSetupName}
          onContinue={this.onContinue}
          dev={dev}
        />
      </Route>
      <Route path={BASE_URL + steps.REVIEW}>
        <TestDriveSetupReview
          key={steps.REVIEW}
          firstName={firstName}
          pronouns={pronouns}
          onContinue={this.onContinue}
          activeAccessMethodName={activeAccessMethodName}
          activeAccessMethodCategory={activeAccessMethodCategory}
          activeSetupSelectionMethod={this.state.activeTestDrive.activeSetupSelectionMethod}
          activeSetupBodySite={this.state.activeTestDrive.activeSetupBodySite}
          activeSetupBodySiteComments={this.state.activeTestDrive.activeSetupBodySiteComments}
          activeSetupAccessories={this.state.activeTestDrive.activeSetupAccessories}
          activeSetupPositioningNotes={this.state.activeTestDrive.activeSetupPositioningNotes}
          activeSetupOtherNotes={this.state.activeTestDrive.activeSetupOtherNotes}
          activeSetupName={this.state.activeTestDrive.activeSetupName}
          dev={dev}
        />
      </Route>
      <Route path={BASE_URL}>
        <Redirect to={BASE_URL + steps.INTRO + searchQuery} />
      </Route>
    </Switch>
    </>
    );
  }
}

TestDriveSetup.propTypes = propTypes;
TestDriveSetup.defaultProps = defaultProps;

// withRouter wrapper provides access to the match, location, and history props (used in onContinue)
export default withRouter(TestDriveSetup);
